/**
 * Persistent "Previewing as <persona>" toolbar (#1383, redesigned #1492,
 * copy + a11y clarified #1892).
 *
 * While an admin is in the view-as-persona PREVIEW (entered from the workspace
 * switcher), this makes the simulated view obvious and NON-TRAPPING: it names
 * the persona and offers a one-click "Exit" that always returns to full Admin.
 * It renders nothing when not previewing.
 *
 * CRITICAL HONESTY (#1892): the copy frames this as a PREVIEW of what the
 * persona sees, NOT a privilege change. A secondary line spells this out in
 * plain language -- "Preview only -- your own access is unchanged" -- because
 * the admin's real token is never altered (this is frontend masking only; no
 * server guard is reduced). The Exit control carries an explicit aria-label so
 * its purpose (return to full admin access) is announced to assistive tech.
 *
 * Layout (#1492): a TOP-MOUNTED, full-width toolbar rendered in NORMAL document
 * flow at the very top of the shell's main column (see AdminLayout / CrmLayout).
 * It is NOT `fixed`/floating -- it occupies real layout height and pushes the
 * header + page content DOWN beneath it, so it can never overlap or obscure the
 * content (the old centred floating pill clipped the dashboard on narrow
 * viewports -- #1492). Colour: a solid navy system bar (matches the navy
 * sidebar, brand-token `bg-navy`) with white text for a clear, high-contrast
 * status treatment in BOTH light and dark mode; the blue brand accent
 * (`--color-accent`) gives the Exit control prominence. Mobile-built (>=44px
 * Exit target, no horizontal overflow).
 */
import { useNavigate } from "react-router-dom";
import { Eye, X } from "lucide-react";
import { usePreviewPersonaOptional } from "../../contexts/previewPersona";
import { previewPersonaLabel } from "../../lib/previewPersona";
import { ViewAsPicker } from "../viewAs/ViewAsPicker";

export function PreviewBanner() {
  const { previewing, persona, exitPreview } = usePreviewPersonaOptional();
  const navigate = useNavigate();

  if (!previewing || !persona) {
    return null;
  }

  const label = previewPersonaLabel(persona);

  function handleExit() {
    exitPreview();
    // Return to the full-admin home so the admin is never stranded on a surface
    // their persona preview navigated them to.
    navigate("/admin/dashboard");
  }

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="preview-banner"
      title="Preview only — you are seeing what this permission type sees. Your own access is unchanged."
      className="flex w-full shrink-0 flex-wrap items-center gap-2 border-b border-white/10 bg-navy px-4 py-2 text-white sm:flex-nowrap sm:px-6"
    >
      <Eye
        className="h-4 w-4 shrink-0 text-brand-accent"
        aria-hidden="true"
      />
      <span className="min-w-0 flex-1 text-xs font-medium sm:text-sm">
        <span className="block truncate">
          Previewing as <span className="font-semibold">{label}</span>
        </span>
        <span className="block truncate text-[0.6875rem] font-normal text-white/75 sm:text-xs">
          Preview only — your own access is unchanged.
        </span>
      </span>
      {/* D2-SEC-011 (#3944): the persona preview shows a permission type's
          LAYOUT with your own data; this picker shows one real agent's data,
          read only. Renders nothing for a user the server will not allow. */}
      <ViewAsPicker />
      <button
        type="button"
        onClick={handleExit}
        aria-label={`Exit preview and return to full admin access (currently previewing as ${label})`}
        className="inline-flex min-h-[44px] shrink-0 items-center gap-1.5 rounded-md bg-brand-accent px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-brand-accent-hover focus:outline-hidden focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-navy sm:text-sm"
      >
        <X className="h-4 w-4" aria-hidden="true" />
        <span>Exit preview</span>
      </button>
    </div>
  );
}
